"use client";

import { useState } from "react";

import { InfiniteScrollComponent } from "@/components/atoms/InfiniteScrollComponent";
import { NavigationBarBySection } from "@/components/molecules/NavigationBarBySection";
import {
  SingleApplicationCardInfo,
  SingleApplicationCardInfoSkeleton,
} from "@/components/molecules/SingleApplicationCardInfo";
import {
  AppDetails,
  AppDetailsSkeleton,
} from "@/components/organisms/AppDetails";
import { AppRateAndReview } from "@/components/organisms/AppRateAndReview";
import AppRateAndReviewSkeleton from "@/components/organisms/AppRateAndReview/appRateAndReviewSkeleton";
import { Benefits, BenefitsSkeleton } from "@/components/organisms/Benefits";
import {
  CarouselApplicationImages,
  CarouselApplicationImagesSkeleton,
} from "@/components/organisms/CarouselApplicationImages";
import { InstallApplicationDrawer } from "@/components/organisms/InstallApplicationDrawer";
import {
  defaultValueFilter,
  defaultValueSorting,
  filterConstants,
  sortConstants,
} from "@/components/pages/SinglePageApp/constants";
import type {
  IReview,
  ISinglePageAppProps,
} from "@/components/pages/SinglePageApp/types";
import { AddOrEditEnum } from "@/components/pages/SinglePageApp/types";
import { useAddReviewMutation } from "@/hooks/apis/mutations/useAddReviewMutation";
import { useDeleteReviewMutation } from "@/hooks/apis/mutations/useDeleteReviewMutation";
import { useDeleteReviewVoteMutation } from "@/hooks/apis/mutations/useDeleteReviewVoteMutation";
import { useHelpfulOrUnhelpfulReviewMutation } from "@/hooks/apis/mutations/useHelpfulOrUnhelpfulReviewMutation";
import { useUpdateReviewMutation } from "@/hooks/apis/mutations/useUpdateReviewMutation";
import { useApplicationByAliasQuery } from "@/hooks/apis/queries/useApplicationByAliasQuery";
import useApplicationRateCountQuery from "@/hooks/apis/queries/useApplicationRateCountQuery/useApplicationRateCountQuery";
import { useApplicationReviewsInfiniteQuery } from "@/hooks/apis/queries/useApplicationReviewsInfiniteQuery";
import { useMostHelpfulAndUnHelpfulReviewQuery } from "@/hooks/apis/queries/useMostHelpfulAndUnHelpfulReviewQuery";
import { useProjectByIdQuery } from "@/hooks/apis/queries/useProjectByIdQuery";
import { useReviewByIdQuery } from "@/hooks/apis/queries/useReviewByIdQuery";
import { useAppRouter } from "@/hooks/common/useAppRouter";
import { useAuthUser } from "@/hooks/common/useAuthUser";
import { useFilterAndSorting } from "@/hooks/common/useFilterAndSorting";
import { useTranslations } from "@/hooks/common/useTranslations";

import { queryParamRedirectedFromDashboard } from "@/common/constants/common";
import type { HelpfulOrUnhelpfulReviewEnum } from "@/common/globalTypes/enums/common";
import { APP_STORE_SUPPORTED_LANGUAGES_ENUM } from "@/common/globalTypes/enums/common";

const SinglePageApp = ({ appAlias, application }: ISinglePageAppProps) => {
  const { appPush } = useAppRouter();
  const { projectId, logIn, redirectHandlerWithSso } = useAuthUser();

  const { currentLocale } = useTranslations();

  const [isOpenInstallAppFolder, setIsOpenInstallAppFolder] = useState(false);
  const {
    currentFilter,
    transformedFiltersValue,
    onChangeFilter,
    transformedSortValues,
    onChangeSort,
    currentSort,
  } = useFilterAndSorting({
    filterConstants,
    sortConstants,
    defaultValueFilter,
    defaultValueSorting,
  });

  const { applicationByAlias, isLoadingApplicationByAlias, refetch } =
    useApplicationByAliasQuery({
      appAlias,
      application,
      projectId,
    });

  const { projectById } = useProjectByIdQuery(false);

  const { id, name, galleryItems, benefits, details, averageRating } =
    applicationByAlias;

  const {
    applicationReviews,
    isInitialLoadingReviews,
    fetchNextPageReviews,
    isFetchingNextPageReviews,
    hasNextPageReviews,
  } = useApplicationReviewsInfiniteQuery({
    applicationId: id,
    filterBy: currentFilter,
    sortBy: currentSort,
  });

  const { addReview, isPending: isLoadingAddReview } = useAddReviewMutation();

  const { updateReview, isPending: isLoadingUpdateReview } =
    useUpdateReviewMutation();

  const { deleteReview, isPending: isLoadingDeleteReview } =
    useDeleteReviewMutation();

  const {
    applicationRateCount,
    isInitialLoadingAppRateCount,
    refetchApplicationRateCount,
  } = useApplicationRateCountQuery({
    applicationId: id,
  });

  const {
    mostHelpfulAndUnHelpfulReviewCounts,
    isInitialLoadingMostHelpfulAndUnHelpfulReview,
  } = useMostHelpfulAndUnHelpfulReviewQuery({ applicationId: id });

  const { helpfulOrUnhelpfulReview } = useHelpfulOrUnhelpfulReviewMutation({
    filterBy: currentFilter,
    sortBy: currentSort,
  });

  const {
    appReview,
    isLoadingAppReview,
    onCloseReviewModal,
    onAddReviewHandler,
    onEditReviewHandler,
    isShowEditOrAddModalReview,
  } = useReviewByIdQuery();

  const { deleteReviewVote } = useDeleteReviewVoteMutation();

  const isLoadingCreateEditReview = isLoadingAddReview || isLoadingUpdateReview;
  const isLoadingSinglePageReviewApp =
    isInitialLoadingAppRateCount ||
    isInitialLoadingMostHelpfulAndUnHelpfulReview;

  const onCreateOrEditReview = async (
    review: IReview,
    addOrEdit: AddOrEditEnum,
    closeModalCallback: () => void,
  ) => {
    const applicationReview = {
      ...review,
      applicationId: id,
    };

    if (addOrEdit === AddOrEditEnum.add) {
      await addReview(applicationReview);
    } else {
      await updateReview(applicationReview);
    }

    await refetchApplicationRateCount();
    await refetch();
    closeModalCallback();
  };

  const onDeleteHandler = async (
    reviewId: number,
    closeModalCallback: () => void,
  ) => {
    await deleteReview({ reviewId });
    await refetchApplicationRateCount();
    await refetch();
    closeModalCallback();
  };

  const helpfulOrUnhelpfulReviewHandler = async (
    reviewId: number,
    helpfulOrUnhelpful: HelpfulOrUnhelpfulReviewEnum,
    isDeleteVote: boolean,
    triggerCallback: () => void,
    // eslint-disable-next-line max-params
  ) => {
    if (isDeleteVote) {
      await deleteReviewVote({ reviewId });
    } else {
      await helpfulOrUnhelpfulReview({ reviewId, helpfulOrUnhelpful });
    }

    triggerCallback();
  };

  const callBackHandlerIfILogged = (isInstalledApp?: boolean) => {
    if (isInstalledApp === undefined) {
      return appPush(`${appAlias}/projects`);
    }

    if (isInstalledApp) {
      return redirectHandlerWithSso(
        "dashboard",
        `${projectById?.name}/applications?app=${appAlias}`,
      );
    }

    return setIsOpenInstallAppFolder(true);
  };

  const getAppHandler = (isInstalledApp?: boolean) => {
    const redirectPath = `${currentLocale === APP_STORE_SUPPORTED_LANGUAGES_ENUM.en ? "" : `/${currentLocale}`}/${appAlias}/${
      isInstalledApp === undefined
        ? "/projects"
        : `?${queryParamRedirectedFromDashboard}=${projectId}`
    }`;

    logIn(redirectPath, () => callBackHandlerIfILogged(isInstalledApp));
  };

  return (
    <div className="flex justify-center items-center w-full">
      <div className="flex flex-col max-w-[1280px] gap-5 w-full">
        {isLoadingApplicationByAlias ? (
          <>
            <SingleApplicationCardInfoSkeleton />
            <CarouselApplicationImagesSkeleton />
            <BenefitsSkeleton />
            <AppDetailsSkeleton />
          </>
        ) : (
          <>
            <SingleApplicationCardInfo
              app={applicationByAlias}
              getAppActionHandler={getAppHandler}
            />
            <CarouselApplicationImages galleryItems={galleryItems} />
            <Benefits appBenefits={benefits} appName={name} />
            <AppDetails appDetails={details} />
            {isLoadingSinglePageReviewApp ? (
              <AppRateAndReviewSkeleton />
            ) : (
              <InfiniteScrollComponent
                hasMore={hasNextPageReviews}
                loadMore={fetchNextPageReviews}
              >
                <AppRateAndReview
                  reviews={applicationReviews}
                  mostHelpfulAndUnHelpfulReviewCounts={
                    mostHelpfulAndUnHelpfulReviewCounts
                  }
                  ratesCount={{
                    rating: averageRating,
                    ratesCount: applicationRateCount,
                  }}
                  reviewHandlers={{
                    onEditReviewHandler,
                    onAddReviewHandler,
                    onCloseReviewModal,
                    helpfulOrUnhelpfulReviewHandler,
                    createOrEditReviewHandler: onCreateOrEditReview,
                    deleteReviewHandler: onDeleteHandler,
                  }}
                  singleReviewState={{
                    isShowEditOrAddModalReview,
                    appReview,
                  }}
                  loadingStates={{
                    isLoadingReviews: isInitialLoadingReviews,
                    isFetchingNextPageReviews,
                    isLoadingCreateEditReview,
                    isLoadingDeleteReview,
                    isLoadingAppReview,
                  }}
                  filterAndSoring={{
                    onChangeFilter,
                    transformedFiltersValue,
                    currentFilter,
                    transformedSortValues,
                    onChangeSort,
                    currentSort,
                  }}
                />
              </InfiniteScrollComponent>
            )}

            <NavigationBarBySection />
            {isOpenInstallAppFolder && (
              <InstallApplicationDrawer
                open={isOpenInstallAppFolder}
                onClose={() => setIsOpenInstallAppFolder(false)}
                appAlias={appAlias}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SinglePageApp;
